import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthLayout } from "../components/AuthLayout";
import { Spinner } from "../components/Loading";
import { request } from "../lib/api";
import { useAuth } from "../lib/auth";

type SessionShape = {
  access_token: string;
  refresh_token?: string;
  user_id: string;
  email: string;
};

export function AuthCallbackPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function complete() {
      try {
        const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
        const query = new URLSearchParams(window.location.search);
        const oauthError =
          hash.get("error_description") ||
          query.get("error_description") ||
          query.get("error");
        if (oauthError) throw new Error(oauthError);

        let accessToken = hash.get("access_token") || "";
        let refreshToken = hash.get("refresh_token") || "";
        let user: { id: string; email: string };

        if (!accessToken && query.get("code")) {
          const exchanged = await request<{
            access_token: string;
            refresh_token?: string;
            user: { id: string; email: string };
          }>(`/api/auth/exchange-code?code=${encodeURIComponent(query.get("code") || "")}`);
          accessToken = exchanged.access_token;
          refreshToken = exchanged.refresh_token || "";
          user = exchanged.user;
        } else {
          if (!accessToken) throw new Error("No authentication session was returned.");
          user = await request<{ id: string; email: string }>(
            "/api/user/profile",
            {},
            accessToken
          );
        }

        if (cancelled) return;
        const session: SessionShape = {
          access_token: accessToken,
          refresh_token: refreshToken,
          user_id: user.id,
          email: user.email
        };
        auth.adoptSession(session);
        history.replaceState(null, "", "/auth/callback");
        navigate("/dashboard", { replace: true });
      } catch (callbackError) {
        if (!cancelled) {
          setError(callbackError instanceof Error ? callbackError.message : "Authentication failed");
        }
      }
    }
    void complete();
    return () => {
      cancelled = true;
    };
  }, [auth.adoptSession, navigate]);

  return (
    <AuthLayout subtitle="Parental Control Dashboard">
      {error ? (
        <>
          <h2>Authentication failed</h2>
          <div className="alert alert-error">{error}</div>
          <Link className="btn btn-secondary btn-block" to="/login">
            Back to Login
          </Link>
        </>
      ) : (
        <div className="loading-panel">
          <Spinner />
          <span className="muted">Validating your LimitX session...</span>
        </div>
      )}
    </AuthLayout>
  );
}
